// U9 API (/api/cleanup): Recently done, Put back and Fresh start. Every POST asks the server for
// one gateway approval over exactly the threads on screen; nothing here touches Gmail. A Fresh
// start run (docs/design/fresh-start.md) is approved whole: the client sends the set's digest
// and the senders to keep, never the thread list.
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import type { RoutineActionResult, WorkView } from "@shared/api";
import type {
  FreshStartNudgeView,
  FreshStartRunRequest,
  FreshStartThread,
  FreshStartThreshold,
  FreshStartView,
  PutBackRequest,
  PutBackRunRequest,
  RecentBatchItem,
  RecentDoneView,
} from "@shared/recent";
import { api } from "@web/lib/api";

const enc = encodeURIComponent;

export const recentUrls = {
  recent: () => "/api/cleanup/recent",
  runSenderItems: (groupId: string, address: string) =>
    `/api/cleanup/recent/runs/${enc(groupId)}/senders/${enc(address)}`,
  putBack: () => "/api/cleanup/put-back",
  putBackRun: (groupId: string) => `/api/cleanup/runs/${enc(groupId)}/put-back`,
  continueRun: (groupId: string) => `/api/cleanup/runs/${enc(groupId)}/continue`,
  freshStart: (threshold?: FreshStartThreshold) =>
    threshold ? `/api/cleanup/fresh-start?threshold=${threshold}` : "/api/cleanup/fresh-start",
  senderThreads: (threshold: FreshStartThreshold, address: string) =>
    `/api/cleanup/fresh-start/senders/${enc(address)}?threshold=${threshold}`,
  freshStartRuns: () => "/api/cleanup/fresh-start/runs",
  freshStartNudge: () => "/api/cleanup/fresh-start/nudge",
};

export const recentApi = {
  recent: (signal?: AbortSignal) => api<RecentDoneView>(recentUrls.recent(), { signal }),
  runSenderItems: (groupId: string, address: string, signal?: AbortSignal) =>
    api<RecentBatchItem[]>(recentUrls.runSenderItems(groupId, address), { signal }),
  putBack: (body: PutBackRequest) =>
    api<RoutineActionResult>(recentUrls.putBack(), { method: "POST", body }),
  putBackRun: (groupId: string, body: PutBackRunRequest = {}) =>
    api<RoutineActionResult>(recentUrls.putBackRun(groupId), { method: "POST", body }),
  continueRun: (groupId: string) =>
    api<{ work: WorkView }>(recentUrls.continueRun(groupId), { method: "POST", body: {} }),
  freshStart: (threshold: FreshStartThreshold | undefined, signal?: AbortSignal) =>
    api<FreshStartView>(recentUrls.freshStart(threshold), { signal }),
  senderThreads: (threshold: FreshStartThreshold, address: string, signal?: AbortSignal) =>
    api<FreshStartThread[]>(recentUrls.senderThreads(threshold, address), { signal }),
  startRun: (body: FreshStartRunRequest) =>
    api<RoutineActionResult>(recentUrls.freshStartRuns(), { method: "POST", body }),
  freshStartNudge: (signal?: AbortSignal) =>
    api<FreshStartNudgeView>(recentUrls.freshStartNudge(), { signal }),
};

export const recentKeys = {
  all: ["cleanup"] as const,
  recent: ["cleanup", "recent"] as const,
  runSenderItems: (groupId: string, address: string) =>
    ["cleanup", "recent", "run", groupId, address] as const,
  freshStart: (threshold: FreshStartThreshold | undefined) =>
    ["cleanup", "fresh-start", threshold ?? "default"] as const,
  senderThreads: (threshold: FreshStartThreshold, address: string) =>
    ["cleanup", "fresh-start", threshold, address] as const,
  /** Under `all`, so a run (invalidateCleanup) refreshes it. */
  freshStartNudge: ["cleanup", "fresh-start-nudge"] as const,
};

export const recentQuery = queryOptions({
  queryKey: recentKeys.recent,
  queryFn: ({ signal }) => recentApi.recent(signal),
  staleTime: 5_000,
});

export const runSenderItemsQuery = (groupId: string, address: string) =>
  queryOptions({
    queryKey: recentKeys.runSenderItems(groupId, address),
    queryFn: ({ signal }) => recentApi.runSenderItems(groupId, address, signal),
    staleTime: 5_000,
  });

export const freshStartQuery = (threshold: FreshStartThreshold | undefined) =>
  queryOptions({
    queryKey: recentKeys.freshStart(threshold),
    queryFn: ({ signal }) => recentApi.freshStart(threshold, signal),
    staleTime: 5_000,
  });

export const senderThreadsQuery = (threshold: FreshStartThreshold, address: string) =>
  queryOptions({
    queryKey: recentKeys.senderThreads(threshold, address),
    queryFn: ({ signal }) => recentApi.senderThreads(threshold, address, signal),
    staleTime: 30_000,
  });

/** Whether to recommend Fresh start before a long round (the feed's one quiet banner). */
export const freshStartNudgeQuery = queryOptions({
  queryKey: recentKeys.freshStartNudge,
  queryFn: ({ signal }) => recentApi.freshStartNudge(signal),
  staleTime: 5 * 60_000,
});

export function invalidateCleanup(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: recentKeys.all });
}
